import { useState, useEffect } from 'react'
import { useLanguage } from '../context/LanguageContext'
import PropertyCard from './PropertyCard'

const NAVY = '#002E53'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"
const API_URL = import.meta.env.VITE_API_URL || ''



/* ─── Skeleton card ───────────────────────────────────────────────────── */
function SkeletonCard() {
  return (
    <div style={{
      borderRadius: '16px', overflow: 'hidden', background: '#fff',
      border: '1px solid rgba(0,46,83,0.08)', boxShadow: '0 4px 16px rgba(0,46,83,0.06)',
    }}>
      <div style={{ height: '220px', background: 'linear-gradient(90deg, #f0f4f8 25%, #e2e8f0 50%, #f0f4f8 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }} />
      <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ height: '20px', width: '70%', background: '#e2e8f0', borderRadius: '6px' }} />
        <div style={{ height: '14px', width: '50%', background: '#e2e8f0', borderRadius: '6px' }} />
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ height: '24px', width: '80px', background: '#e2e8f0', borderRadius: '6px' }} />
          <div style={{ height: '24px', width: '60px', background: '#e2e8f0', borderRadius: '6px' }} />
        </div>
        <div style={{ height: '40px', background: '#e2e8f0', borderRadius: '10px', marginTop: '8px' }} />
      </div>
    </div>
  )
}

function FeaturedListings() {
  const { t } = useLanguage()
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(3)

  // Manage responsive items per page
  useEffect(() => {
    const updateSize = () => {
      if (window.innerWidth <= 600) setItemsPerPage(1)
      else if (window.innerWidth <= 1024) setItemsPerPage(2)
      else setItemsPerPage(3)
    }
    updateSize()
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [])

  useEffect(() => {
    fetch(`${API_URL}/api/offplan?limit=10`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch offplan properties')
        return res.json()
      })
      .then(data => {
        setProjects(data.projects || [])
        setLoading(false)
      })
      .catch(err => {
        console.error('[FeaturedListings] Error fetching offplan properties:', err)
        setLoading(false)
      })
  }, [])

  // Create combined array including the final Discover More item
  const sliderItems = loading ? Array.from({ length: 6 }).map((_, i) => ({ isSkeleton: true, id: `skel_${i}` })) 
    : [...projects, { isShowMore: true, id: 'show_more_item' }]

  const totalPages = Math.ceil(sliderItems.length / itemsPerPage)
  
  // Ensure we don't end up on an empty page if resize changes totalPages
  useEffect(() => {
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(totalPages - 1)
    }
  }, [totalPages, currentPage])

  const visibleItems = sliderItems.slice(
    currentPage * itemsPerPage,
    currentPage * itemsPerPage + itemsPerPage
  )

  const goToPrev = () => setCurrentPage((prev) => Math.max(0, prev - 1))
  const goToNext = () => setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))

  return (
    <>
      <div style={{ backgroundColor: '#FFFFFF', width: '100%' }}>
        <style>{`
          .featured-heading-row {
            display: flex;
            flex-direction: row;
            gap: 15px;
            align-items: flex-end;
            flex-wrap: wrap;
          }
          .featured-heading-desc {
            flex: 0 1 400px;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
          }
          .feat-slider-grid { 
            display: grid; 
            gap: 24px; 
            width: 100%;
          }
          .feat-mobile-arrows { display: none; gap: 10px; margin-top: 16px; }
          .feat-arrow-btn { width:42px;height:42px;border-radius:50%;border:1.5px solid rgba(0,46,83,0.18);background:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all 0.2s;font-size:24px;color:#002E53; flex-shrink:0; padding-bottom: 4px; }
          .feat-arrow-btn:hover:not(:disabled) { background:#002E53; color:#fff; border-color:#002E53; }
          .feat-arrow-btn:disabled { opacity: 0.4; cursor: not-allowed; }
          @media (max-width: 1024px) {
            .featured-heading-row { flex-direction: column; align-items: flex-start; gap: 12px; }
            .featured-heading-desc { flex: 1 1 auto; padding-left: 0 !important; }
          }
          @media (max-width: 600px) {
            .feat-mobile-arrows { display: flex; }
            .featured-slider-arrow { display: none !important; }
          }
          @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        `}</style>

        <div style={{
          maxWidth: '1300px',
          margin: '0 auto',
          padding: '60px 20px 40px 20px',
          boxSizing: 'border-box',
        }}>
          {/* Heading row */}
          <div className="featured-heading-row" style={{ marginBottom: '40px' }}>
            <div style={{ flex: '1 1 auto' }}>
              <div style={{ marginBottom: '8px' }}>
                <span style={{
                  backgroundColor: 'rgba(197,160,89,0.1)',
                  color: '#C5A059',
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  fontWeight: 700,
                  borderRadius: '5px',
                  padding: '5px 8px',
                  display: 'inline-block',
                }}>{ t('featured.tag') }</span>
              </div>
              <h2 style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: 'clamp(23px, 3.5vw, 44px)',
                fontWeight: 600,
                lineHeight: '1.2em',
                letterSpacing: '-0.3px',
                color: '#002E53',
                margin: 0,
                maxWidth: '550px',
              }}>
                { t('featured.heading') }
              </h2>
            </div>

            <div className="featured-heading-desc">
              <p style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: '1.6em',
                color: '#002E53',
                margin: 0,
              }}>
                { t('featured.desc') }
              </p>
              
              {/* Mobile top arrows */}
              <div className="feat-mobile-arrows">
                <button className="feat-arrow-btn" onClick={goToPrev} disabled={currentPage === 0} aria-label="Previous">‹</button>
                <button className="feat-arrow-btn" onClick={goToNext} disabled={currentPage === totalPages - 1} aria-label="Next">›</button>
              </div>
            </div>
          </div>

          {/* Slider */}
          <div className="shortcode_slider_wrapper">
            <div className="featured-slider-outer">
              {/* Left arrow */}
              <button
                className={`featured-slider-arrow featured-slider-arrow--prev${currentPage === 0 ? ' disabled' : ''}`}
                onClick={goToPrev}
                disabled={currentPage === 0}
                aria-label="Previous properties"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5"></path>
                  <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
              </button>

              {/* Cards wrapper */}
              <div className="shortcode_slider_list arrow_class_sideways" data-items-per-row={itemsPerPage} data-auto="0">
                <div className="feat-slider-grid" style={{ gridTemplateColumns: `repeat(${itemsPerPage}, 1fr)` }}>
                  {visibleItems.map(item => {
                    if (item.isSkeleton) return <SkeletonCard key={item.id} />
                    if (item.isShowMore) return (
                      <a href="/offplan" key={item.id} style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        borderRadius: '16px', background: '#fafbfc', border: '2px dashed rgba(0,46,83,0.15)',
                        color: NAVY, textDecoration: 'none', minHeight: '380px', height: '100%',
                        transition: 'all 0.3s ease', cursor: 'pointer', boxSizing: 'border-box'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f0f4f8'; e.currentTarget.style.borderColor = 'rgba(0,46,83,0.3)'; e.currentTarget.style.transform = 'translateY(-4px)' }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#fafbfc'; e.currentTarget.style.borderColor = 'rgba(0,46,83,0.15)'; e.currentTarget.style.transform = 'translateY(0)' }}
                      >
                        <div style={{ fontSize: '40px', marginBottom: '16px', color: GOLD }}>➔</div>
                        <div style={{ fontFamily: FONT, fontSize: '20px', fontWeight: 700 }}>Discover More</div>
                        <div style={{ fontFamily: FONT, fontSize: '14px', color: '#6b7a8d', marginTop: '8px', maxWidth: '200px', textAlign: 'center', lineHeight: '1.5' }}>
                          Explore our full portfolio of exclusive off-plan projects
                        </div>
                      </a>
                    )
                    return <PropertyCard key={item.id} project={item} />
                  })}
                </div>
              </div>

              {/* Right arrow */}
              <button
                className={`featured-slider-arrow featured-slider-arrow--next${currentPage === totalPages - 1 ? ' disabled' : ''}`}
                onClick={goToNext}
                disabled={currentPage === totalPages - 1}
                aria-label="Next properties"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12H19"></path>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </button>
            </div>

            {/* Pagination dots */}
            <div className="featured-slider-dots">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`featured-slider-dot${i === currentPage ? ' active' : ''}`}
                  onClick={() => setCurrentPage(i)}
                  aria-label={`Go to page ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default FeaturedListings
